import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  patientQuotesFromRpc,
  quoteTrackingListFromRpc,
  quoteTrackingFromRpc,
  type ListQuoteTrackingInput,
  type UpdateQuoteTrackingInput,
} from "cadendar-shared";
import { QuotesService } from "../../generated/cadendar-rpc/cadendar/quotes/v1/quotes_pb";
import {
  cadendarRpcTransport,
  shouldRetryCadendarRpcRequest,
} from "../main/components/cadendarRpc";
import useOwner from "../users/hooks/useOwner";

const client = createClient(QuotesService, cadendarRpcTransport);
export const quotesQueryKey = ["cadendar-rpc", "quotes"] as const;
export function useRefreshQuotes() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: quotesQueryKey });
}
export function usePatientQuotes(
  patientId: string | undefined,
  options: { refetchInterval?: number } = {},
) {
  const ownerId = useOwner();
  return useQuery({
    queryKey: [...quotesQueryKey, ownerId, "patient", patientId],
    enabled: !!ownerId && !!patientId,
    queryFn: async () =>
      patientQuotesFromRpc(
        await client.getPatientQuotes({
          ownerId: ownerId!,
          patientId: patientId!,
        }),
      ),
    retry: shouldRetryCadendarRpcRequest,
    ...options,
  });
}
export function useQuoteTracking(
  input: ListQuoteTrackingInput,
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  const ownerId = useOwner();
  return useQuery({
    queryKey: [...quotesQueryKey, ownerId, "tracking", input],
    enabled: !!ownerId && options.enabled !== false,
    refetchInterval: options.refetchInterval,
    queryFn: async () =>
      quoteTrackingListFromRpc(
        await client.listTracking({
          ...input,
          ownerId: ownerId!,
          sortDirection:
            input.sortDirection ?? (input.sort === "newest" ? "desc" : "asc"),
        }),
      ),
    retry: shouldRetryCadendarRpcRequest,
  });
}
export function useUpdateQuoteTracking(onSuccess: () => void) {
  const ownerId = useOwner();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateQuoteTrackingInput) => {
      if (!ownerId) throw new Error("Sélectionnez un praticien.");
      return quoteTrackingFromRpc(
        await client.updateTracking({
          ...input,
          ownerId,
          effectiveDate: input.effectiveDate ?? undefined,
          deliveryDate: input.deliveryDate ?? undefined,
          acceptanceDate: input.acceptanceDate ?? undefined,
        }),
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quotesQueryKey });
      onSuccess();
    },
  });
}
